import axios from "axios";
import { supabase } from "../supabaseClient"; // 1. Import your Supabase client

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// 2. Removed withCredentials: true. It is no longer needed for JWTs.
const api = axios.create({ baseURL: API_BASE, timeout: 90000 });

// 3. Add an interceptor to fetch and attach the Supabase token
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (session?.access_token) {
    // .trim() strips out any hidden newlines, carriage returns, or trailing spaces!
    const cleanToken = session.access_token.trim();
    config.headers.Authorization = `Bearer ${cleanToken}`;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    // Guard against non-JSON error bodies (e.g. an HTML 502 page leaking through)
    if (detail.trim().startsWith("<") || detail.length > 300)
      return "The service is busy right now. Please try again in a minute.";
    return detail;
  }
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Map raw Supabase / API auth errors to friendly, action-oriented messages.
 * Falls back to formatApiError when nothing else matches.
 */
export function formatAuthError(err) {
  const raw =
    err?.response?.data?.detail ||
    err?.message ||
    err?.error_description ||
    err ||
    "";
  const msg = (typeof raw === "string" ? raw : formatApiError(raw)).toLowerCase();
  if (!msg) return "Something went wrong. Please try again.";
  if (msg.includes("invalid login") || msg.includes("invalid credentials") || msg.includes("invalid_grant"))
    return "That email and password don't match. Try again or reset your password.";
  if (msg.includes("email not confirmed") || msg.includes("not confirmed"))
    return "Please confirm your email — check your inbox for the verification link.";
  if (msg.includes("user already registered") || msg.includes("already registered") || msg.includes("already exists"))
    return "An account with this email already exists. Try signing in instead.";
  if (msg.includes("password should be at least") || msg.includes("weak password") || msg.includes("password"))
    return msg.includes("6") || msg.includes("8")
      ? "Password is too short — use at least 8 characters."
      : "Password is too weak. Try a longer mix of letters and numbers.";
  if (msg.includes("rate limit") || msg.includes("too many"))
    return "Too many attempts. Please wait a minute and try again.";
  if (msg.includes("email") && msg.includes("valid"))
    return "That doesn't look like a valid email address.";
  if (msg.includes("network") || msg.includes("failed to fetch"))
    return "Can't reach the server. Check your connection and try again.";
  return typeof raw === "string" ? raw : formatApiError(raw);
}

export default api;
